import {
  type Component,
  For,
  Show,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
} from 'solid-js';
import { Portal } from 'solid-js/web';
import { useNavigate } from '@solidjs/router';
import { ArrowRight, Search, Settings as SettingsIcon } from 'lucide-solid';
import { SETTINGS_NODES, type SettingsNode } from '~/shared/api/settings-api';
import { SETTINGS_MODULE_LABEL as MODULE_LABEL } from './settingsModuleLabel';
import { useAuth } from '~/shared/api/auth-context';
import { canOpenSettingsNode, menuEntry } from '~/shared/access/sections';

const MODULE_ACCENT: Record<SettingsNode['module'], string> = {
  club: '#9CA3AF',
  operations: '#087DE4',
  'pricing-loyalty': '#6AD10F',
  communications: '#ACD317',
  integrations: '#FFD12B',
  'access-security': '#FF8C42',
  system: '#6B7280',
  fitness: 'var(--fitness)',
};

const score = (q: string, n: SettingsNode): number => {
  const norm = q.toLowerCase().trim();
  if (!norm) return 1;
  const hay = `${n.title} ${n.subtitle} ${MODULE_LABEL[n.module]} ${n.keywords.join(' ')}`.toLowerCase();
  if (n.title.toLowerCase().startsWith(norm)) return 100;
  if (hay.includes(norm)) return 50;
  let cursor = 0;
  for (const ch of norm) {
    const found = hay.indexOf(ch, cursor);
    if (found === -1) return 0;
    cursor = found + 1;
  }
  return 10;
};

export const SettingsSpotlight: Component = () => {
  const [open, setOpen] = createSignal(false);
  const [query, setQuery] = createSignal('');
  const [activeIdx, setActiveIdx] = createSignal(0);
  const navigate = useNavigate();
  const { session } = useAuth();
  const permissions = (): readonly string[] => session()?.user.permissions ?? [];
  const canOpenSettings = (): boolean => menuEntry('/settings', permissions()) !== null;
  let inputRef: HTMLInputElement | undefined;

  const handleKey = (e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === ',') {
      if (!canOpenSettings()) return;
      e.preventDefault();
      setOpen((v) => !v);
      setQuery('');
      setActiveIdx(0);
      return;
    }
    if (open() && e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  onMount(() => window.addEventListener('keydown', handleKey));
  onCleanup(() => window.removeEventListener('keydown', handleKey));

  createEffect(() => {
    if (open()) {
      queueMicrotask(() => inputRef?.focus());
    }
  });

  const results = createMemo(() => {
    const q = query();
    return SETTINGS_NODES
      .filter((n) => !n.comingSoon && canOpenSettingsNode(n, permissions()))
      .map((n) => ({ n, s: score(q, n) }))
      .filter((r) => r.s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 14)
      .map((r) => r.n);
  });

  createEffect(() => {
    const list = results();
    if (activeIdx() >= list.length) setActiveIdx(0);
  });

  const go = (n: SettingsNode) => {
    setOpen(false);
    navigate(n.href);
  };

  const onInputKey = (e: KeyboardEvent) => {
    const list = results();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(list.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const sel = list[activeIdx()];
      if (sel) go(sel);
    }
  };

  return (
    <Show when={open()}>
      <Portal>
        <div
          class="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] px-4"
          style={{
            background: 'rgba(8, 12, 22, 0.55)',
            '-webkit-backdrop-filter': 'blur(8px)',
            'backdrop-filter': 'blur(8px)',
          }}
          onClick={() => setOpen(false)}
        >
          <div
            class="w-full max-w-[640px] rounded-[16px] glass overflow-hidden"
            style={{
              'box-shadow': '0 30px 80px -10px rgba(0,0,0,0.5), inset 0 0 0 1px var(--color-border-hairline)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <header
              class="flex items-center gap-3 px-4 py-3 border-b-hairline"
              style={{
                background: 'color-mix(in srgb, var(--color-bg-glass-strong) 92%, transparent)',
              }}
            >
              <SettingsIcon class="h-4 w-4 text-tertiary" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Поиск по настройкам…"
                class="flex-1 h-9 bg-transparent text-[14px] outline-none placeholder:text-tertiary"
                value={query()}
                onInput={(e) => { setQuery(e.currentTarget.value); setActiveIdx(0); }}
                onKeyDown={onInputKey}
              />
              <kbd class="px-1.5 py-0.5 rounded bg-white/10 text-[10.5px] text-tertiary">esc</kbd>
            </header>

            <Show
              when={results().length > 0}
              fallback={
                <div class="px-4 py-10 text-center">
                  <Search class="h-5 w-5 mx-auto text-tertiary mb-2" />
                  <p class="text-[13px] text-tertiary">
                    Ничего не нашлось по запросу «{query()}».
                  </p>
                </div>
              }
            >
              <ul class="max-h-[60vh] overflow-y-auto p-2">
                <For each={results()}>
                  {(n, idx) => {
                    const active = () => idx() === activeIdx();
                    return (
                      <li>
                        <button
                          type="button"
                          onClick={() => go(n)}
                          onMouseEnter={() => setActiveIdx(idx())}
                          class="w-full text-left px-3 py-2 rounded-[10px] flex items-center gap-3 transition-colors"
                          style={{
                            background: active()
                              ? `color-mix(in srgb, ${MODULE_ACCENT[n.module]} 14%, transparent)`
                              : 'transparent',
                          }}
                        >
                          <span
                            class="w-7 h-7 rounded-[8px] shrink-0 flex items-center justify-center text-[10px] font-mono"
                            style={{
                              background: `color-mix(in srgb, ${MODULE_ACCENT[n.module]} 18%, transparent)`,
                              color: MODULE_ACCENT[n.module],
                            }}
                          >
                            ⚙
                          </span>
                          <div class="flex-1 min-w-0">
                            <p class="text-[13px] font-medium leading-tight">{n.title}</p>
                            <p class="text-[11px] text-tertiary mt-0.5 truncate">
                              {MODULE_LABEL[n.module]} · {n.subtitle}
                            </p>
                          </div>
                          <ArrowRight class="h-3.5 w-3.5 text-tertiary shrink-0" />
                        </button>
                      </li>
                    );
                  }}
                </For>
              </ul>
            </Show>

            <footer
              class="px-4 py-2 text-[10.5px] text-tertiary flex items-center gap-3 border-t-hairline"
              style={{
                background: 'color-mix(in srgb, var(--color-bg-glass-strong) 92%, transparent)',
              }}
            >
              <span><kbd class="px-1 py-0.5 rounded bg-white/10 text-[10px]">↑↓</kbd> навигация</span>
              <span><kbd class="px-1 py-0.5 rounded bg-white/10 text-[10px]">enter</kbd> открыть</span>
              <span class="ml-auto"><kbd class="px-1 py-0.5 rounded bg-white/10 text-[10px]">⌘ ,</kbd> вызвать</span>
            </footer>
          </div>
        </div>
      </Portal>
    </Show>
  );
};
